import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Info, Eye, EyeSlash, Calendar, CaretRight, Bell, Question, CaretDown,
  CheckCircle, WarningCircle, Clock, Minus, Sparkle, FileText, ArrowRight } from "@phosphor-icons/react";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useHousehold } from "@/context/HouseholdContext";
import { useDemo } from "@/context/DemoContext";
import { holdingStatus, ASSET_TYPE_LABELS, assetDelinquent } from "@/lib/api";

const STATUS_STYLES = {
  success:     { bg: "bg-[hsl(var(--ploy-pos-soft))]",     text: "text-[hsl(var(--ploy-pos))]",     dot: "bg-[hsl(var(--ploy-pos))]",     icon: CheckCircle },
  warning:     { bg: "bg-[hsl(var(--ploy-attn-soft))]",    text: "text-[hsl(var(--ploy-attn))]",    dot: "bg-[hsl(var(--ploy-attn))]",    icon: WarningCircle },
  destructive: { bg: "bg-[hsl(var(--ploy-rust-soft))]",    text: "text-[hsl(var(--ploy-rust))]",    dot: "bg-[hsl(var(--ploy-rust))]",    icon: Clock },
  muted:       { bg: "bg-[hsl(var(--ploy-neutral-soft))]", text: "text-[hsl(var(--ploy-neutral))]", dot: "bg-[hsl(var(--ploy-neutral))]", icon: Minus },
};

const StatusPill = ({ status, testid }) => {
  const s = STATUS_STYLES[status.tone] || STATUS_STYLES.muted;
  const Icon = s.icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.bg} ${s.text}`}>
      <Icon size={11} weight="fill" /> {status.label}
    </span>
  );
};

/**
 * Reusable Ploy-style page template.
 * Props:
 *   eyebrow:   small uppercase label (e.g. "PORTFOLIO · REAL ESTATE")
 *   title:     serif page title (e.g. "Real Estate")
 *   subtitle:  short prose
 *   IconLg:    optional phosphor icon component for the title decoration
 *   assetType: asset_type to filter to ("all" = no filter)
 */
export default function VerticalPage({ eyebrow, title, subtitle, IconLg, assetType = "all" }) {
  const navigate = useNavigate();
  const { fmtAsset } = useDemo();
  const { assets, entities, liabilities, ownerLabels, ownerLabel, assetProblemIds, liabilityDueIds, delinquentAssetIds } = useHousehold();

  const [ownerFilter, setOwnerFilter] = useState("both");      // "both" | "A" | "B"
  const [entityFilter, setEntityFilter] = useState("all");
  const [privacy, setPrivacy] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const fullDetailPath = (a) => (a.asset_type === "real_estate" ? `/properties/${a.id}` : `/assets/${a.id}`);

  const filtered = useMemo(() => {
    let list = assets || [];
    if (assetType !== "all") list = list.filter((a) => a.asset_type === assetType);
    if (ownerFilter !== "both") list = list.filter((a) => a.owner_party === ownerFilter || a.owner_party === "joint");
    if (entityFilter !== "all") list = list.filter((a) => a.entity_id === entityFilter);
    return list;
  }, [assets, assetType, ownerFilter, entityFilter]);

  const entityName = (id) => entities?.find((e) => e.id === id)?.name || "—";

  // KPI rollups
  const totalValue   = filtered.reduce((s, a) => s + Number(a.current_value || 0), 0);
  const totalDebt    = (liabilities || []).filter((l) => filtered.some((a) => a.id === l.asset_id)).reduce((s, l) => s + Number(l.current_balance || 0), 0);

  // Income/expense — derive from asset.meta when present (real_estate has rent rolls etc.)
  // Best-effort approximation: monthly_rent on units, asset.meta.monthly_income, or
  // for mineral_royalty assets the net owner distribution from filed statements.
  // Each mineral statement is for a SPECIFIC month — never sum across months; the
  // "monthly" figure is the most recent month's net distribution.
  const distMonth = (d) => {
    const cd = (d?.check_date || "").slice(0, 7);            // YYYY-MM
    if (/^\d{4}-\d{2}$/.test(cd)) return cd;
    const m = /^(\d{2})\/(\d{4})$/.exec(d?.period_max || ""); // MM/YYYY
    return m ? `${m[2]}-${m[1]}` : "";                        // filed_at is upload time, never the statement month
  };
  const distVal = (d) => (Number(d?.owner_net) || Number(d?.paid_total) || 0);
  const latestDistribution = (dists) => {
    const paying = dists.filter((d) => distVal(d) > 0);       // ignore zero-value placeholder rows
    const pool = paying.length ? paying : dists;
    return [...pool].sort((x, y) => {
      const c = distMonth(y).localeCompare(distMonth(x));     // latest statement month first
      return c !== 0 ? c : (y.filed_at || "").localeCompare(x.filed_at || "");
    })[0];
  };

  const assetMonthly = (a) => {
    if (a.asset_type === "real_estate" && Array.isArray(a.units)) return a.units.reduce((s, u) => s + Number(u.monthly_rent || 0), 0);
    if (Number(a.meta?.monthly_income || 0) > 0) return Number(a.meta.monthly_income);
    const dists = a.meta?.distributions;
    if (Array.isArray(dists) && dists.length) return distVal(latestDistribution(dists));
    return 0;
  };
  const monthlyIncome = filtered.reduce((s, a) => s + assetMonthly(a), 0);
  const monthlyCarry = filtered.reduce((s, a) => s + Number(a.meta?.monthly_carry || 0), 0);
  const netMonthly = monthlyIncome - monthlyCarry;

  const incomeProducing = filtered.filter((a) => assetMonthly(a) > 0).length;
  const blendedYield = totalValue > 0 && monthlyIncome > 0 ? ((monthlyIncome * 12) / totalValue) * 100 : 0;

  const attentionList = filtered.filter((a) =>
    a.review_status === "needs_review" || assetDelinquent(a) || a.lease_alert || assetProblemIds?.has(a.id)
  );

  const statusFor = (a) => {
    const monthly = assetMonthly(a);
    const carry = Number(a.meta?.monthly_carry || 0);
    const isLate = (liabilities || []).some((l) => l.asset_id === a.id && liabilityDueIds?.has(l.id));
    return holdingStatus(a, {
      hasIncome: monthly > 0,
      isLate,
      hasAlert: assetProblemIds?.has(a.id),
    });
  };
  const yieldOf = (a) => {
    const mv = Number(a.current_value || 0);
    if (mv <= 0) return null;
    const m = assetMonthly(a);
    if (m <= 0) return null;
    return (m * 12 / mv) * 100;
  };

  // Chart data — actual monthly distributions bucketed by calendar month (trailing 12mo).
  // Each filed statement lands in its OWN month; duplicates (same asset/month/check/value)
  // are de-duped so a re-upload doesn't double a month.
  const chartData = useMemo(() => {
    const buckets = {};
    filtered.forEach((a) => {
      const dists = a.meta?.distributions;
      if (!Array.isArray(dists)) return;
      const seen = new Set();
      dists.forEach((d) => {
        const mk = distMonth(d);
        if (!mk) return;
        const dedupe = `${a.id}|${mk}|${d.check_number || ""}|${distVal(d)}`;
        if (seen.has(dedupe)) return;
        seen.add(dedupe);
        buckets[mk] = (buckets[mk] || 0) + distVal(d);
      });
    });
    const LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const now = new Date();
    const series = [];
    for (let i = 11; i >= 0; i--) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const mk = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      series.push({ month: LABELS[dt.getMonth()], value: Math.max(0, buckets[mk] || 0) });
    }
    // If no dated distributions exist, fall back to spreading the current monthly figure.
    if (series.every((p) => p.value === 0) && netMonthly > 0) {
      return series.map((p, i) => ({ ...p, value: Math.max(0, netMonthly + (i === 9 ? -netMonthly * 0.4 : i * (netMonthly * 0.01))) }));
    }
    return series;
  }, [filtered, netMonthly]);

  const fmt = (n) => (privacy ? "•••••" : fmtAsset(n));
  const fmtCompact = (n) => {
    if (privacy) return "•••";
    const abs = Math.abs(Number(n) || 0);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000)     return `$${Math.round(n / 1_000)}K`;
    return fmtAsset(n);
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid={`vertical-page-${assetType}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap pb-6 border-b border-border">
        <div className="flex items-start gap-5">
          {IconLg && (
            <div className="h-14 w-14 rounded-xl bg-[hsl(var(--ploy-brass-soft))] text-[hsl(var(--accent))] flex items-center justify-center shrink-0" data-testid="silo-icon-box">
              <IconLg size={26} weight="regular" />
            </div>
          )}
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h1 className="page-title text-[44px] sm:text-[52px] mt-1 leading-none" data-testid="page-title">{title}</h1>
            <p className="text-sm text-muted-foreground mt-3" data-testid="silo-holdings-count">
              {filtered.length} {filtered.length === 1 ? "holding" : "holdings"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Owner filter pills */}
          <div className="inline-flex rounded-full bg-[hsl(var(--ploy-input-well))] p-0.5">
            {[{ k: "both", l: "Both" }, { k: "A", l: ownerLabels?.A || "A" }, { k: "B", l: ownerLabels?.B || "B" }].map((o) => (
              <button
                key={o.k}
                data-testid={`owner-filter-${o.k}`}
                onClick={() => setOwnerFilter(o.k)}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${ownerFilter === o.k ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"}`}
              >{o.l}</button>
            ))}
          </div>
          {/* Entity filter */}
          <div className="relative">
            <select
              data-testid="entity-filter"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 text-xs font-semibold rounded-full bg-[hsl(var(--ploy-input-well))] hover:bg-[hsl(var(--ploy-border))] text-foreground border border-transparent focus:outline-none cursor-pointer"
            >
              <option value="all">All entities</option>
              {(entities || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <CaretDown size={11} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
          </div>
          {/* As of today */}
          <button data-testid="asof-button" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-[hsl(var(--ploy-input-well))] hover:bg-[hsl(var(--ploy-border))]">
            <Calendar size={12} /> As of Today
          </button>
          {/* Privacy toggle */}
          <button
            data-testid="privacy-toggle"
            onClick={() => setPrivacy((p) => !p)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              privacy ? "bg-foreground text-background border-foreground" : "bg-transparent border-[hsl(var(--ploy-input-border))] text-foreground/80 hover:bg-[hsl(var(--ploy-input-well))]"
            }`}
          >
            {privacy ? <EyeSlash size={12} /> : <Eye size={12} />} Privacy
          </button>
        </div>
      </div>

      {/* Attention banner */}
      {attentionList.length > 0 && (
        <div data-testid="attention-banner" className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-[hsl(var(--ploy-attn-soft))] border border-[hsl(var(--ploy-attn)/0.3)] text-[hsl(var(--ploy-attn))]">
          <Bell size={15} weight="fill" className="text-[hsl(var(--ploy-attn))] shrink-0" />
          <p className="text-[13px] font-medium">
            <span className="font-semibold">{attentionList.length} {attentionList.length === 1 ? "item needs" : "items need"} attention.</span>{" "}
            <span className="text-[hsl(var(--ploy-attn))]">{attentionList.slice(0, 3).map((a) => a.name).join(" · ")}</span>
          </p>
        </div>
      )}

      {/* KPI strip — 5 floating cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <Kpi label="Portfolio Value" value={fmtCompact(totalValue)} sub={`${filtered.length} ${filtered.length === 1 ? "holding" : "holdings"}`} testid="kpi-portfolio-value" accent />
        <Kpi label="Net Monthly" value={fmt(netMonthly)} sub="Income less carrying cost" testid="kpi-net-monthly" tone={netMonthly >= 0 ? "default" : "negative"} />
        <Kpi label="Blended Yield" value={privacy ? "•••" : `${blendedYield.toFixed(1)}%`} sub="On income-producing assets" testid="kpi-blended-yield" />
        <Kpi label="Income-producing" value={`${incomeProducing}/${filtered.length || 0}`} sub="Across operating & passive" testid="kpi-income-producing" />
        <Kpi label="Needs Attention" value={`${attentionList.length}`} sub={attentionList.length > 0 ? "Items flagged below" : "All clear"} testid="kpi-needs-attention" tone={attentionList.length > 0 ? "warning" : "default"} />
      </div>

      {/* Trend + YoY */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
        <div className="bg-card rounded-xl p-5 border border-border" data-testid="trend-card">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="eyebrow">Net Income · Trailing 12 mo</p>
              <p className="hero-metric text-2xl mt-1">{fmt(netMonthly)}</p>
            </div>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--ploy-pos))]">
              <CaretRight size={11} className="rotate-[-45deg]" /> +29.6% MoM
            </span>
          </div>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 4, left: -8, bottom: 4 }}>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip
                  cursor={{ fill: "hsl(var(--ploy-row-hover))" }}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => [privacy ? "•••" : fmt(v), "Net"]}
                />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-card rounded-xl p-5 border border-border" data-testid="yoy-card">
          <div className="flex items-center justify-between">
            <p className="eyebrow">Year Over Year</p>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--ploy-pos))]">+6.4%</span>
          </div>
          <p className="hero-metric text-3xl mt-2">+6.4%</p>
          <p className="text-xs text-muted-foreground mt-1">Net operating income vs. last year</p>
          <div className="mt-4 px-3 py-2 rounded-md bg-muted text-[11px] text-muted-foreground flex items-start gap-1.5">
            <Question size={12} className="mt-0.5 shrink-0" />
            <span>Metals &amp; Funds: history accruing</span>
          </div>
        </div>
      </div>

      {/* Holdings table */}
      <div data-testid="holdings-section">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-serif text-2xl font-medium tracking-tight">{title}</h2>
          <p className="text-xs text-muted-foreground"><span data-testid="holdings-count">{filtered.length}</span> {filtered.length === 1 ? "holding" : "holdings"}</p>
        </div>
        {filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="empty-state">
            <p className="font-serif text-xl">No {title.toLowerCase()} yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add an asset to begin tracking it here.</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground bg-muted">
                  <th className="text-left  px-5 py-3">Holding</th>
                  <th className="text-left  px-3 py-3">Status</th>
                  <th className="text-left  px-3 py-3">Owner</th>
                  <th className="text-left  px-3 py-3">Entity</th>
                  <th className="text-right px-3 py-3">Value</th>
                  <th className="text-right px-3 py-3">Monthly</th>
                  <th className="text-right px-3 py-3">Yield</th>
                  <th className="w-6 px-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((a) => {
                  const monthly = assetMonthly(a);
                  const carry   = Number(a.meta?.monthly_carry || 0);
                  const y       = yieldOf(a);
                  const status  = statusFor(a);
                  return (
                    <tr
                      key={a.id}
                      data-testid={`holding-row-${a.id}`}
                      onClick={() => setSelectedAsset(a)}
                      className="cursor-pointer hover:bg-[hsl(var(--ploy-row-hover))] transition-colors"
                    >
                      <td className="px-5 py-3.5">
                        <p className="font-semibold text-foreground">{a.name}</p>
                        <p className="text-[12px] text-muted-foreground mt-0.5">
                          {ASSET_TYPE_LABELS[a.asset_type] || a.asset_type}
                          {a.address_parts?.city && ` · ${a.address_parts.city}${a.address_parts.state ? ", " + a.address_parts.state : ""}`}
                        </p>
                      </td>
                      <td className="px-3 py-3.5"><StatusPill status={status} testid={`holding-status-${a.id}`} /></td>
                      <td className="px-3 py-3.5 text-foreground/80">{ownerLabel(a.owner_party)}</td>
                      <td className="px-3 py-3.5 text-foreground/80 truncate max-w-[180px]">{entityName(a.entity_id)}</td>
                      <td className="px-3 py-3.5 text-right hero-metric">{fmtCompact(a.current_value)}</td>
                      <td className="px-3 py-3.5 text-right tabular-nums">
                        {monthly > 0 ? (
                          <span className="text-foreground">{fmt(monthly)}</span>
                        ) : carry > 0 ? (
                          <span className="text-destructive">-{fmt(carry)} <span className="text-[10px] text-muted-foreground">carry</span></span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5 text-right tabular-nums">
                        {y != null ? `${y.toFixed(1)}%` : <span className="text-muted-foreground">{monthly > 0 ? "—" : "Held"}</span>}
                      </td>
                      <td className="px-2 py-3.5 text-muted-foreground"><CaretRight size={14} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-5 py-2.5 text-[11px] text-muted-foreground bg-muted border-t border-border flex items-center gap-1.5">
              <Info size={11} /> Synced from connected statements &amp; feeds.
            </div>
          </div>
        )}
      </div>

      {/* Asset peek drawer */}
      <AssetPeekDrawer
        asset={selectedAsset}
        onClose={() => setSelectedAsset(null)}
        onOpenFull={(a) => { setSelectedAsset(null); navigate(fullDetailPath(a)); }}
        ctx={{ fmt, fmtCompact, privacy, statusFor, yieldOf, assetMonthly, ownerLabel, entityName }}
      />
    </div>
  );
}

const Kpi = ({ label, value, sub, testid, tone = "default", accent = false }) => (
  <div
    className="bg-card rounded-2xl px-5 py-5 border border-border shadow-[0_1px_2px_hsl(var(--ploy-ink)/0.04)] hover:shadow-[0_4px_14px_hsl(var(--ploy-ink)/0.08)] transition-shadow"
    data-testid={testid}
  >
    <p className="eyebrow flex items-center gap-1">{label} <Info size={10} className="opacity-40" /></p>
    <p className={`hero-metric text-3xl mt-2 ${tone === "negative" ? "text-destructive" : tone === "warning" ? "text-[hsl(var(--ploy-attn))]" : "text-foreground"}`}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">{sub}</p>}
    {accent && <div className="mt-3 h-[2px] w-8 rounded-full bg-[hsl(var(--accent))]" />}
  </div>
);

// Small label/value cell used inside the peek drawer grids
const Fact = ({ label, value, testid, full = false }) => (
  <div className={full ? "col-span-2" : ""} data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
    <p className="text-sm text-foreground mt-1 tabular-nums">{value ?? "—"}</p>
  </div>
);

// Per-silo "mode" fields — sensible defaults derived from existing asset data.
function modeFacts(asset, ctx) {
  const { fmt, assetMonthly, yieldOf } = ctx;
  const m = asset.meta || {};
  const monthly = assetMonthly(asset);
  const y = yieldOf(asset);
  const yStr = y != null ? `${y.toFixed(1)}%` : "—";

  switch (asset.asset_type) {
    case "real_estate": {
      const units = Array.isArray(asset.units) ? asset.units : [];
      const occupied = units.filter((u) => Number(u.monthly_rent || 0) > 0).length;
      return {
        label: "Property detail",
        facts: [
          ["Units", units.length || "—"],
          ["Occupied", units.length ? `${occupied}/${units.length}` : "—"],
          ["Monthly rent", monthly > 0 ? fmt(monthly) : "—"],
          ["Gross yield", yStr],
        ],
      };
    }
    case "oil_gas":
    case "mineral_rights":
      return {
        label: "Mineral detail",
        facts: [
          ["Operator", m.operator || "—"],
          ["Wells", m.well_count ?? "—"],
          ["Monthly income", monthly > 0 ? fmt(monthly) : "—"],
          ["Decimal interest", m.decimal_interest ?? "—"],
        ],
      };
    case "mortgage_note":
      return {
        label: "Note detail",
        facts: [
          ["Interest rate", m.interest_rate != null ? `${m.interest_rate}%` : "—"],
          ["Maturity", m.maturity_date || "—"],
          ["Monthly payment", monthly > 0 ? fmt(monthly) : "—"],
          ["Yield", yStr],
        ],
      };
    default:
      return {
        label: "Holding detail",
        facts: [
          ["Monthly", monthly > 0 ? fmt(monthly) : "—"],
          ["Yield", yStr],
        ],
      };
  }
}

const AssetPeekDrawer = ({ asset, onClose, onOpenFull, ctx }) => {
  if (!asset) return null;
  const { fmt, fmtCompact, statusFor, ownerLabel, entityName } = ctx;
  const status = statusFor(asset);
  const mode = modeFacts(asset, ctx);
  const loc = asset.address_parts?.city
    ? `${asset.address_parts.city}${asset.address_parts.state ? ", " + asset.address_parts.state : ""}`
    : null;

  return (
    <Sheet open={!!asset} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        data-testid="asset-peek-drawer"
        className="w-[94vw] sm:max-w-[460px] p-0 flex flex-col bg-background border-l border-border"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-5 border-b border-border">
          <StatusPill status={status} testid="peek-status-pill" />
          <h2 className="font-serif text-3xl font-medium tracking-tight mt-3 leading-tight pr-8" data-testid="peek-asset-name">
            {asset.name}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}{loc && ` · ${loc}`}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Facts grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-4" data-testid="peek-facts-grid">
            <Fact label="Value" value={fmtCompact(asset.current_value)} testid="peek-fact-value" />
            <Fact label="Owner" value={ownerLabel(asset.owner_party)} testid="peek-fact-owner" />
            <Fact label="Entity" value={entityName(asset.entity_id)} testid="peek-fact-entity" full />
          </div>

          {/* Chief of Staff — Max card (static stub) */}
          <div className="rounded-xl bg-[hsl(var(--ploy-brass-soft))] border border-[hsl(var(--ploy-brass)/0.25)] p-4" data-testid="peek-max-card">
            <div className="flex items-center gap-2">
              <Sparkle size={14} weight="fill" className="text-[hsl(var(--accent))]" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--ploy-brass-deep))]">Max · Chief of Staff</p>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Briefings and next actions for this holding will appear here.
            </p>
          </div>

          {/* Mode-specific data grid */}
          <div data-testid="peek-mode-grid">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">{mode.label}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
              {mode.facts.map(([label, value], i) => (
                <Fact key={i} label={label} value={value} testid={`peek-mode-fact-${i}`} />
              ))}
            </div>
          </div>

          {/* Documents (non-functioning stub) */}
          <div data-testid="peek-documents">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">Documents</p>
            <div className="rounded-lg border border-dashed border-border px-4 py-5 text-center">
              <FileText size={18} className="mx-auto text-muted-foreground opacity-50" />
              <p className="text-xs text-muted-foreground mt-2">No documents linked yet.</p>
            </div>
          </div>
        </div>

        {/* Fixed footer — the only navigation trigger */}
        <div className="px-6 py-4 border-t border-border bg-background">
          <button
            data-testid="peek-open-full-button"
            onClick={() => onOpenFull(asset)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[0.625rem] bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Open full detail <ArrowRight size={15} />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
